import { css } from '@emotion/react';
import { faCircleDot } from '@fortawesome/pro-regular-svg-icons/faCircleDot';
import {
  faChevronRight, faEarthAmericas,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { Shape } from '~/_shared/baseComponents/labelInShape/labelInShapeComponent';
import { useTranslation } from '~/_shared/utils/hooks';
import { useIsMapInteractionActive } from '~/_shared/utils/hooks/useIsMapInteractionActive';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useItemsVisibleOnPresentationalMap } from '~/app/maptiveApp/useItemsVisibleOnPresentationalMap.hook';
import { MainMenuItems } from '~/mainMenu/mainMenuItem.enum';
import type {
  OverlappingMapElement, OverlappingUIPanelTabs,
} from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUI.types';
import { useGetBoundaryFillInfo } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryFillInfo';
import { boundaryTerritoryGroupActivate } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  useActiveBoundaryTerritoryGroupsSelector, usePrimaryBoundaryGroupBoundaryTerritoryGroups,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { boundaryTerritoryGroupHighlightStarted } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import { OverlappingUIPanelComponent } from './overlappingUIPanel.component';
import { useOverlappingElementsController } from './useOverlappingElementsController.hook';

const tooltipContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export const OverlappingUIPanelContainer: FC = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const isMapInteractionActive = useIsMapInteractionActive();
  const isPresentational = useIsMapPresentationalSelector();
  const isBoundaryToolAllowedOnPresentationalMaps = useItemsVisibleOnPresentationalMap().has(MainMenuItems.Boundary);
  const isBoundaryToolEnabled = !isPresentational || isBoundaryToolAllowedOnPresentationalMaps;
  const getBoundaryFillInfo = useGetBoundaryFillInfo();
  const { overlappingElements } = useOverlappingElementsController();
  const primaryBoundaryGroupBoundaryTerritoryGroups = usePrimaryBoundaryGroupBoundaryTerritoryGroups();
  const activeBTGs = useActiveBoundaryTerritoryGroupsSelector();
  const activePrimaryBtg = primaryBoundaryGroupBoundaryTerritoryGroups.find(btg => {
    return activeBTGs.includes(btg);
  });

  const altFillItems: readonly OverlappingMapElement[] = useMemo(() => {
    return primaryBoundaryGroupBoundaryTerritoryGroups.map((boundaryTerritoryGroup, index): OverlappingMapElement | null => {
      if (!boundaryTerritoryGroup) {
        return null;
      }
      const {
        fillOrderNumber,
        backgroundColor,
        foregroundColor,
        entities: {
          shortenedMainEntity,
          shortenedSubEntity,
        },
      } = getBoundaryFillInfo(boundaryTerritoryGroup, index);

      return {
        shape: Shape.circle,
        active: activePrimaryBtg === boundaryTerritoryGroup,
        fontColor: foregroundColor,
        backgroundColor,
        label: fillOrderNumber,
        name: (
          <span css={tooltipContentStyles}>
            <span>
              {shortenedMainEntity}
            </span>
            {shortenedSubEntity && (
              <FontAwesomeIcon
                icon={faChevronRight}
                size="xs"
              />
            )}
            {shortenedSubEntity && (
              <span>
                {shortenedSubEntity}
              </span>
            )}
          </span>
        ),
        onClick: () => {
          dispatch(boundaryTerritoryGroupActivate(boundaryTerritoryGroup));
          dispatch(boundaryTerritoryGroupHighlightStarted(boundaryTerritoryGroup.boundaryGroupId));
        },
      };
    }).filter(notNullsy);
  },
  [activePrimaryBtg, dispatch, getBoundaryFillInfo, primaryBoundaryGroupBoundaryTerritoryGroups]);

  const tabs: readonly OverlappingUIPanelTabs[] = useMemo(() => {
    return [
      {
        id: 'proximities',
        elements: overlappingElements,
        icon: faCircleDot,
        tooltipText: t('overlappingUi.proximities'),
      },
      isBoundaryToolEnabled ? {
        id: 'territoryGroups',
        elements: altFillItems,
        icon: faEarthAmericas,
        tooltipText: t('overlappingUi.altFills'),
      } : null,
    ].filter(notNullsy);
  }, [altFillItems, isBoundaryToolEnabled, overlappingElements, t]);

  if (isMapInteractionActive) {
    return null;
  }

  return (
    <OverlappingUIPanelComponent overlappingTabs={tabs} />
  );
};
